/**
 * Procedural hero props for the Venom Altar (chunky, RuneScape-style silhouettes), built from
 * primitives with baked vertex colours (painted AO: darker at the base, lighter on top). Every
 * generator returns a non-indexed BufferGeometry in yards with `position`, `normal` and `color`,
 * origin at the base centre, facing +z. The builder merges them into the per-cluster prop mesh
 * (UVs point at the white texel of the prop atlas), so they cost no extra draw calls.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { VENOM_PALETTE as P } from './theme';

type Col = number | readonly [number, number, number];

const tmpC = new THREE.Color();
const toLinear = (c: Col): [number, number, number] => {
  if (typeof c === 'number') {
    tmpC.setHex(c);
    return [tmpC.r, tmpC.g, tmpC.b];
  }
  return [c[0], c[1], c[2]];
};

/** Bake a colour (with a vertical AO gradient over [y0, y1]) into a non-indexed copy. */
function paint(geo: THREE.BufferGeometry, color: Col, ao: [number, number] = [0.7, 1.08], range?: [number, number]): THREE.BufferGeometry {
  const g = geo.index ? geo.toNonIndexed() : geo;
  if (g !== geo) geo.dispose();
  g.deleteAttribute('uv');
  const pos = g.attributes['position'] as THREE.BufferAttribute;
  g.computeBoundingBox();
  const y0 = range ? range[0] : g.boundingBox!.min.y;
  const y1 = range ? range[1] : g.boundingBox!.max.y;
  const [r, gg, b] = toLinear(color);
  const col = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const t = y1 > y0 ? Math.min(1, Math.max(0, (pos.getY(i) - y0) / (y1 - y0))) : 1;
    const k = ao[0] + (ao[1] - ao[0]) * t;
    col[i * 3] = r * k;
    col[i * 3 + 1] = gg * k;
    col[i * 3 + 2] = b * k;
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  if (!g.attributes['normal']) g.computeVertexNormals();
  return g;
}

const M = new THREE.Matrix4();
const Q = new THREE.Quaternion();
const E = new THREE.Euler();
const S = new THREE.Vector3();
const T = new THREE.Vector3();
/** Transform helper: translate, rotate (euler XYZ), scale. */
function xf(geo: THREE.BufferGeometry, t: [number, number, number], r: [number, number, number] = [0, 0, 0], s: [number, number, number] = [1, 1, 1]): THREE.BufferGeometry {
  M.compose(T.set(...t), Q.setFromEuler(E.set(...r)), S.set(...s));
  geo.applyMatrix4(M);
  return geo;
}

function combine(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const g = mergeGeometries(parts, false)!;
  parts.forEach((p) => p.dispose());
  return g;
}

const box = (w: number, h: number, d: number): THREE.BufferGeometry => new THREE.BoxGeometry(w, h, d).translate(0, h / 2, 0);
const cyl = (rt: number, rb: number, h: number, seg = 10): THREE.BufferGeometry => new THREE.CylinderGeometry(rt, rb, h, seg).translate(0, h / 2, 0);
const cone = (r: number, h: number, seg = 8): THREE.BufferGeometry => new THREE.ConeGeometry(r, h, seg).translate(0, h / 2, 0);
const sphere = (r: number, ws = 10, hs = 7): THREE.BufferGeometry => new THREE.SphereGeometry(r, ws, hs);

const SAND = P.stoneSand;
const SAND_L = P.stoneSandLight;
const SAND_D = P.stoneSandShadow;
const JADE = P.jade;
const JADE_L = P.jadeLight;
const GOLD = P.gold;
const BRONZE = 0xd99a4e;
const WOOD = P.wood;
const IRON = 0x3a3a40;
const BONE = P.bone;
const BLOOD = P.blood;

/** Serpent body along a curve as a tube (scales painted with a jade belly stripe via colour). */
function serpentTube(points: THREE.Vector3[], radius: number, color: Col, tubular = 48, radial = 9, taper = 0.45): THREE.BufferGeometry {
  const curve = new THREE.CatmullRomCurve3(points);
  const tube = new THREE.TubeGeometry(curve, tubular, radius, radial, false);
  // Taper toward the tail (curve start).
  const pos = tube.attributes['position'] as THREE.BufferAttribute;
  const uv = tube.attributes['uv'] as THREE.BufferAttribute;
  const P0 = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    const u = uv.getX(i);
    const k = taper + (1 - taper) * Math.min(1, u * 1.6);
    const c = curve.getPointAt(Math.min(1, u), P0);
    pos.setXYZ(i, c.x + (pos.getX(i) - c.x) * k, c.y + (pos.getY(i) - c.y) * k, c.z + (pos.getZ(i) - c.z) * k);
  }
  tube.computeVertexNormals();
  return paint(tube, color, [0.75, 1.05]);
}

/**
 * Hooded cobra head: a broad hood plate tipped toward the viewer (readable from the high camera),
 * snout, jade eyes, fangs and a jade hood marking. Origin at the neck, facing +z.
 */
function cobraHead(scale: number, body: Col, eye: Col = JADE_L): THREE.BufferGeometry[] {
  const s = scale;
  const tilt = 0.55;
  const hood = paint(xf(sphere(1, 14, 9), [0, 0.6 * s, 0.05 * s], [tilt, 0, 0], [1.25 * s, 1.05 * s, 0.34 * s]), body, [0.75, 1.1]);
  const mark = paint(xf(new THREE.TorusGeometry(0.5 * s, 0.07 * s, 5, 18, Math.PI * 1.2), [0, 0.62 * s, 0.22 * s], [tilt, 0, -Math.PI * 0.1 + Math.PI], [1.1, 1.0, 0.6]), JADE, [0.9, 1.05]);
  const head = paint(xf(sphere(1, 12, 8), [0, 1.0 * s, 0.42 * s], [0.35, 0, 0], [0.48 * s, 0.36 * s, 0.66 * s]), body, [0.85, 1.12]);
  const brow = paint(xf(box(0.7 * s, 0.12 * s, 0.4 * s), [0, 1.18 * s, 0.38 * s], [0.35, 0, 0]), body, [1, 1.1]);
  const eyes = [-1, 1].map((sx) => paint(xf(sphere(0.11 * s, 6, 4), [0.25 * s * sx, 1.13 * s, 0.72 * s]), eye, [1, 1]));
  const fangs = [-1, 1].map((sx) => paint(xf(cone(0.06 * s, 0.34 * s, 5), [0.14 * s * sx, 0.86 * s, 0.9 * s], [Math.PI, 0, 0]), BONE, [1, 1]));
  return [hood, mark, head, brow, ...eyes, ...fangs];
}

const GEN: Record<string, () => THREE.BufferGeometry> = {
  brazier: () => {
    const bowl = paint(cyl(0.8, 0.38, 0.55, 12).translate(0, 1.33, 0), BRONZE, [0.7, 1.1]);
    const rim = paint(new THREE.TorusGeometry(0.8, 0.08, 6, 18).rotateX(Math.PI / 2).translate(0, 1.88, 0), GOLD, [1, 1]);
    const coal = paint(new THREE.SphereGeometry(0.62, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2).scale(1, 0.3, 1).translate(0, 1.78, 0), 0x3a1206, [1, 1]);
    const legs = [0, 1, 2].map((i) => {
      const a = (i / 3) * Math.PI * 2;
      const leg = new THREE.CylinderGeometry(0.07, 0.1, 1.5, 6);
      leg.rotateZ(Math.cos(a) * 0.3).rotateX(-Math.sin(a) * 0.3).translate(Math.cos(a) * 0.42, 0.72, Math.sin(a) * 0.42);
      return paint(leg, 0x5a4a3a, [0.6, 1]);
    });
    return combine([bowl, rim, coal, ...legs]);
  },
  cobra: () => {
    // Stepped plinth, a fat coiled base, the body rearing up into a broad hood (art bible §5A).
    const plinth = paint(box(2.3, 0.55, 2.3), SAND_D, [0.6, 0.95]);
    const step = paint(box(1.9, 0.25, 1.9).translate(0, 0.55, 0), SAND, [0.85, 1]);
    const band = paint(box(1.95, 0.08, 1.95).translate(0, 0.8, 0), GOLD, [1, 1]);
    const coil1 = paint(new THREE.TorusGeometry(0.66, 0.3, 9, 20).rotateX(Math.PI / 2).translate(0, 1.12, 0), SAND, [0.75, 1.05]);
    const coil2 = paint(new THREE.TorusGeometry(0.46, 0.26, 9, 18).rotateX(Math.PI / 2).translate(0.06, 1.55, 0.04), SAND, [0.8, 1.05]);
    const body = serpentTube([new THREE.Vector3(0.42, 1.55, -0.2), new THREE.Vector3(0.12, 2.15, -0.05), new THREE.Vector3(-0.04, 2.75, 0.12), new THREE.Vector3(0, 3.15, 0.2)], 0.34, SAND, 26, 10, 0.95);
    const belly = serpentTube([new THREE.Vector3(0.42, 1.6, -0.02), new THREE.Vector3(0.12, 2.2, 0.15), new THREE.Vector3(-0.04, 2.8, 0.33), new THREE.Vector3(0, 3.15, 0.38)], 0.16, SAND_L, 20, 6, 0.95);
    const head = cobraHead(1.0, SAND).map((g) => xf(g, [0, 2.95, 0.2]));
    return combine([plinth, step, band, coil1, coil2, body, belly, ...head]);
  },
  ruined_gate: () => {
    // A half-buried temple gate: two broken pillars, a cracked lintel fragment, closed doors.
    const parts: THREE.BufferGeometry[] = [];
    parts.push(paint(box(2.0, 3.2, 1.8).translate(-4.4, 0, 0), SAND, [0.6, 1.05]));
    parts.push(paint(box(2.0, 2.2, 1.8).translate(4.4, 0, 0), SAND, [0.6, 1.05]));
    parts.push(paint(xf(box(1.6, 0.9, 1.5), [4.1, 2.15, 0.1], [0, 0.2, 0.35]), SAND_L, [0.85, 1.05]));
    parts.push(paint(xf(box(5.6, 0.9, 1.5), [-1.6, 2.6, -0.1], [0, 0, -0.1]), SAND_L, [0.85, 1.05]));
    parts.push(paint(box(6.6, 2.5, 0.5).translate(0, 0, -0.35), WOOD, [0.55, 0.95]));
    parts.push(paint(box(6.8, 0.22, 0.6).translate(0, 0.6, -0.3), IRON, [0.9, 1]));
    parts.push(paint(box(6.8, 0.22, 0.6).translate(0, 1.8, -0.3), IRON, [0.9, 1]));
    parts.push(...cobraHead(0.7, JADE).map((g) => xf(g, [-4.4, 3.1, 0.55])));
    parts.push(paint(xf(box(1.2, 0.7, 1.0), [1.0, 0, 1.4], [0, 0.6, 0.2]), SAND_D, [0.7, 1]));
    parts.push(paint(xf(box(0.9, 0.5, 0.8), [2.2, 0, 1.9], [0.2, 1.1, 0]), SAND, [0.7, 1]));
    return combine(parts);
  },
  cage_top: () => {
    const parts: THREE.BufferGeometry[] = [];
    for (const [x, z] of [
      [-2.3, -1.4],
      [2.3, -1.4],
      [-2.3, 1.4],
      [2.3, 1.4],
    ] as const)
      parts.push(paint(box(0.3, 2.5, 0.3).translate(x, 0, z), WOOD, [0.6, 1]));
    parts.push(paint(box(4.9, 0.25, 0.3).translate(0, 2.35, 1.4), WOOD, [0.9, 1]));
    parts.push(paint(box(4.9, 0.25, 0.3).translate(0, 2.35, -1.4), WOOD, [0.9, 1]));
    for (let i = -2; i <= 2; i++) parts.push(paint(box(0.12, 0.12, 3.0).translate(i * 1.1, 2.4, 0), IRON, [1, 1]));
    return combine(parts);
  },
  straw: () => {
    const parts: THREE.BufferGeometry[] = [];
    const base = paint(new THREE.SphereGeometry(1, 12, 5, 0, Math.PI * 2, 0, Math.PI / 2).scale(1.0, 0.22, 0.85), 0xb59a55, [0.6, 1.0]);
    parts.push(base);
    for (let i = 0; i < 7; i++) {
      const a = i * 0.9;
      parts.push(paint(xf(box(0.06, 0.04, 0.9), [Math.cos(a) * 0.6, 0.12, Math.sin(a) * 0.5], [0, a * 1.7, 0.1]), 0xcbb46a, [1, 1]));
    }
    return combine(parts);
  },
  snake: () => {
    const pts: THREE.Vector3[] = [];
    for (let i = 0; i <= 14; i++) {
      const a = i * 0.75;
      const r = 0.75 - i * 0.04;
      pts.push(new THREE.Vector3(Math.cos(a) * r, 0.18 + i * 0.02, Math.sin(a) * r));
    }
    pts.push(new THREE.Vector3(0.05, 0.6, 0.1));
    const body = serpentTube(pts, 0.14, 0x4f7a3a, 40, 7, 0.4);
    const head = paint(xf(sphere(0.17, 8, 6), [0.05, 0.68, 0.2], [0, 0, 0], [1, 0.8, 1.4]), 0x4f7a3a, [0.9, 1.05]);
    return combine([body, head]);
  },
  mound: () => paint(new THREE.SphereGeometry(1.5, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2).scale(1.1, 0.7, 0.95), P.floorDirt, [0.6, 0.95]),
  rack: () => {
    const parts: THREE.BufferGeometry[] = [];
    parts.push(paint(box(4.2, 0.2, 0.35).translate(0, 0.7, 0), WOOD, [0.8, 1]));
    parts.push(paint(box(4.2, 0.2, 0.35).translate(0, 2.0, -0.2), WOOD, [0.8, 1]));
    for (const x of [-1.9, 1.9]) parts.push(paint(xf(box(0.25, 2.4, 0.25), [x, 0, -0.1], [-0.12, 0, 0]), WOOD, [0.6, 1]));
    for (let i = 0; i < 6; i++) {
      const x = -1.5 + i * 0.6;
      parts.push(paint(xf(cyl(0.05, 0.05, 2.6, 5), [x, 0, 0.1], [-0.18, 0, 0]), WOOD, [0.8, 1]));
      parts.push(paint(xf(cone(0.1, 0.4, 4), [x, 2.55, -0.35], [-0.18, 0, 0]), 0xa8b0b8, [1, 1.1]));
    }
    return combine(parts);
  },
  egg: () => {
    const g = new THREE.SphereGeometry(0.35, 10, 8).scale(1, 1.32, 1).translate(0, 0.42, 0);
    const pg = paint(g, BONE, [0.75, 1.08]);
    // Jade speckles.
    const col = pg.attributes['color'] as THREE.BufferAttribute;
    const pos = pg.attributes['position'] as THREE.BufferAttribute;
    const j = toLinear(JADE);
    for (let i = 0; i < pos.count; i++) {
      const n = Math.sin(pos.getX(i) * 37 + pos.getY(i) * 23) * Math.sin(pos.getZ(i) * 41 + pos.getY(i) * 13);
      if (n > 0.55) col.setXYZ(i, j[0], j[1], j[2]);
    }
    return pg;
  },
  eggshell: () => {
    const a = paint(new THREE.SphereGeometry(0.34, 10, 6, 0, Math.PI * 2, Math.PI * 0.45, Math.PI * 0.55).scale(1, 1.2, 1).translate(0, 0.38, 0), BONE, [0.7, 1]);
    const b = paint(xf(new THREE.SphereGeometry(0.3, 8, 5, 0, Math.PI * 2, 0, Math.PI * 0.4), [0.5, 0.02, 0.2], [Math.PI * 0.9, 0, 0.3]), BONE, [0.8, 1]);
    return combine([a, b]);
  },
  coil: () => {
    // Colossal coiled stone serpent: two ground loops and a body rearing into a hooded head.
    const pts: THREE.Vector3[] = [];
    for (let i = 0; i <= 26; i++) {
      const a = i * 0.48;
      const rx = 5.6 - i * 0.12;
      pts.push(new THREE.Vector3(Math.cos(a) * rx, 0.9 + i * 0.05, Math.sin(a) * rx * 0.36));
    }
    pts.push(new THREE.Vector3(0.6, 3.0, 0.2), new THREE.Vector3(0.2, 4.6, 0.5), new THREE.Vector3(0, 5.4, 0.8));
    const body = serpentTube(pts, 0.95, SAND, 140, 10, 0.3);
    const band = serpentTube(pts.map((p) => p.clone().add(new THREE.Vector3(0, 0.55, 0))), 0.42, JADE, 140, 6, 0.3);
    const head = cobraHead(1.9, SAND).map((g) => xf(g, [0, 5.0, 0.7]));
    const base = paint(box(12.5, 0.5, 4.0).translate(0, 0, -0.2), SAND_D, [0.6, 0.9]);
    return combine([base, body, band, ...head]);
  },
  spout: () => {
    const head = cobraHead(0.5, SAND).map((g) => xf(g, [0, -0.5, 0], [0.6, 0, 0]));
    const plate = paint(box(1.1, 1.1, 0.25).translate(0, -0.55, -0.2), SAND_D, [0.8, 1]);
    return combine([plate, ...head]);
  },
  idol: () => {
    const parts: THREE.BufferGeometry[] = [];
    parts.push(paint(box(3.2, 0.5, 3.2), SAND_D, [0.65, 1]));
    parts.push(paint(box(2.6, 0.5, 2.6).translate(0, 0.5, 0), SAND, [0.8, 1]));
    parts.push(paint(box(2.0, 0.5, 2.0).translate(0, 1.0, 0), SAND_L, [0.85, 1.05]));
    parts.push(paint(new THREE.TorusGeometry(0.62, 0.25, 8, 18).rotateX(Math.PI / 2).translate(0, 1.72, 0), GOLD, [0.8, 1.1]));
    parts.push(serpentTube([new THREE.Vector3(0.3, 1.8, -0.2), new THREE.Vector3(0.1, 2.5, 0), new THREE.Vector3(0, 3.2, 0.15)], 0.24, GOLD, 18, 8, 0.9));
    parts.push(...cobraHead(0.85, GOLD, JADE_L).map((g) => xf(g, [0, 3.0, 0.1])));
    return combine(parts);
  },
  drum: () => {
    const body = paint(cyl(0.95, 0.85, 1.1, 14).translate(0, 0.15, 0), 0x7a4a2c, [0.6, 1]);
    const skin = paint(cyl(0.97, 0.97, 0.08, 14).translate(0, 1.25, 0), 0xd8c49a, [1, 1]);
    const band = paint(cyl(0.99, 0.99, 0.14, 14).translate(0, 0.9, 0), BLOOD, [1, 1]);
    const feet = [0, 1, 2].map((i) => paint(xf(box(0.22, 0.3, 0.22), [Math.cos(i * 2.1) * 0.7, 0, Math.sin(i * 2.1) * 0.7]), WOOD, [0.7, 1]));
    return combine([body, skin, band, ...feet]);
  },
  dais: () => {
    const parts: THREE.BufferGeometry[] = [];
    parts.push(paint(box(7.0, 0.5, 3.8), SAND_D, [0.65, 1]));
    parts.push(paint(box(6.0, 0.5, 3.0).translate(0, 0.5, -0.2), SAND, [0.8, 1]));
    parts.push(paint(box(5.0, 0.5, 2.4).translate(0, 1.0, -0.3), SAND_L, [0.85, 1.05]));
    parts.push(paint(box(2.6, 0.8, 1.3).translate(0, 1.5, -0.3), SAND, [0.8, 1.05]));
    parts.push(paint(box(2.8, 0.06, 1.4).translate(0, 2.3, -0.3), BLOOD, [1, 1]));
    parts.push(paint(box(0.9, 0.9, 0.05).translate(0, 1.45, 0.37), BLOOD, [0.9, 1]));
    parts.push(paint(xf(box(2.8, 0.12, 0.12), [0, 2.32, 0.4]), GOLD, [1, 1]));
    return combine(parts);
  },
  fang_jaw: () => {
    // Two curved fangs framing the far wall, under a hooded serpent head slab.
    const parts: THREE.BufferGeometry[] = [];
    for (const sx of [-1, 1]) {
      const pts = [new THREE.Vector3(3.4 * sx, 0, 0), new THREE.Vector3(3.6 * sx, 2.2, 0.4), new THREE.Vector3(3.0 * sx, 4.2, 0.6), new THREE.Vector3(2.2 * sx, 5.2, 0.3)];
      const curve = new THREE.CatmullRomCurve3(pts);
      const tube = new THREE.TubeGeometry(curve, 24, 0.7, 8, false);
      const pos = tube.attributes['position'] as THREE.BufferAttribute;
      const uv = tube.attributes['uv'] as THREE.BufferAttribute;
      const C = new THREE.Vector3();
      for (let i = 0; i < pos.count; i++) {
        const u = uv.getX(i);
        const k = 0.15 + 0.85 * (1 - u) ** 0.7;
        curve.getPointAt(Math.min(1, u), C);
        pos.setXYZ(i, C.x + (pos.getX(i) - C.x) * k, C.y + (pos.getY(i) - C.y) * k, C.z + (pos.getZ(i) - C.z) * k);
      }
      tube.computeVertexNormals();
      parts.push(paint(tube, BONE, [0.75, 1.08]));
    }
    parts.push(paint(box(8.4, 1.2, 1.6).translate(0, 5.0, -0.2), SAND, [0.8, 1.05]));
    parts.push(...cobraHead(1.6, SAND, JADE_L).map((g) => xf(g, [0, 5.4, 0.0])));
    parts.push(paint(box(8.8, 0.25, 1.8).translate(0, 6.2, -0.2), GOLD, [1, 1]));
    return combine(parts);
  },
  // +dungeon2: monastery / mine dressing --------------------------------------------------
  fountain: () => {
    // Octagonal basin with a rearing cobra spout in the middle (the cloister landmark).
    const parts: THREE.BufferGeometry[] = [];
    parts.push(paint(cyl(3.5, 3.6, 0.9, 8), SAND_D, [0.65, 1]));
    parts.push(paint(new THREE.TorusGeometry(3.25, 0.32, 6, 8).rotateX(Math.PI / 2).rotateY(Math.PI / 8).translate(0, 0.95, 0), SAND_L, [0.9, 1.05]));
    parts.push(paint(cyl(3.0, 3.0, 0.05, 8).translate(0, 0.7, 0), P.waterPoison, [1, 1]));
    parts.push(paint(cyl(0.9, 1.2, 1.6, 10).translate(0, 0.6, 0), SAND, [0.7, 1]));
    parts.push(serpentTube([new THREE.Vector3(0.6, 2.0, -0.5), new THREE.Vector3(-0.3, 3.0, -0.2), new THREE.Vector3(0.1, 4.0, 0.2), new THREE.Vector3(0, 4.6, 0.5)], 0.38, JADE, 28, 9, 0.7));
    parts.push(...cobraHead(1.05, JADE, GOLD).map((g) => xf(g, [0, 4.3, 0.45])));
    return combine(parts);
  },
  bookcase: () => {
    // Tall scroll case: a wooden carcass, four shelves of scroll bundles and codices.
    const parts: THREE.BufferGeometry[] = [];
    const W = 3.2;
    const H = 3.6;
    const D = 0.9;
    parts.push(paint(box(W, H, 0.12).translate(0, 0, -D / 2 + 0.06), 0x4a3220, [0.6, 0.9]));
    for (const sx of [-1, 1]) parts.push(paint(box(0.18, H, D).translate((sx * (W - 0.18)) / 2, 0, 0), WOOD, [0.7, 1.05]));
    for (let k = 0; k <= 4; k++) parts.push(paint(box(W, 0.12, D).translate(0, (k * (H - 0.12)) / 4, 0), WOOD, [0.85, 1.05]));
    parts.push(paint(box(W + 0.3, 0.25, D + 0.2).translate(0, H, 0), WOOD, [0.9, 1.1]));
    const cols = [0x8c3a2e, 0x3f6f5c, 0xb48a4a, 0x5b4a6e, 0xd8ccb0];
    let n = 0;
    for (let k = 0; k < 4; k++) {
      let x = -W / 2 + 0.3;
      while (x < W / 2 - 0.35) {
        const w = 0.16 + ((n * 37) % 7) * 0.025;
        const h = 0.45 + ((n * 53) % 5) * 0.06;
        const y = 0.12 + (k * (H - 0.12)) / 4;
        if ((n * 29) % 11 === 3) {
          parts.push(paint(xf(cyl(0.12, 0.12, D * 0.8, 6), [x + 0.1, y + 0.13, -D * 0.4], [Math.PI / 2, 0, 0]), BONE, [0.9, 1]));
          x += 0.3;
        } else {
          parts.push(paint(box(w, h, D * 0.75).translate(x + w / 2, y, 0), cols[n % cols.length]!, [0.75, 1.05]));
          x += w + 0.03;
        }
        n++;
      }
    }
    return combine(parts);
  },
  lectern: () => {
    const parts: THREE.BufferGeometry[] = [];
    parts.push(paint(box(0.9, 0.2, 0.9), WOOD, [0.7, 1]));
    parts.push(paint(cyl(0.16, 0.2, 1.3, 8).translate(0, 0.2, 0), WOOD, [0.7, 1.05]));
    parts.push(paint(xf(box(1.2, 0.12, 0.85), [0, 1.55, 0], [-0.45, 0, 0]), WOOD, [0.9, 1.1]));
    parts.push(paint(xf(box(1.0, 0.1, 0.7), [0, 1.66, 0.02], [-0.45, 0, 0]), BONE, [1, 1]));
    parts.push(paint(xf(box(0.06, 0.12, 0.72), [0, 1.7, 0.03], [-0.45, 0, 0]), BLOOD, [1, 1]));
    parts.push(paint(cyl(0.12, 0.14, 0.35, 8).translate(0.75, 0, 0.25), SAND_L, [0.9, 1]));
    return combine(parts);
  },
  mine_cart: () => {
    // Ore cart on a short run of rails (the mine wing's signature prop).
    const parts: THREE.BufferGeometry[] = [];
    for (const sx of [-1, 1]) parts.push(paint(box(0.14, 0.12, 5.0).translate(sx * 0.65, 0.12, 0), IRON, [0.8, 1.1]));
    for (let k = -2; k <= 2; k++) parts.push(paint(box(1.8, 0.12, 0.3).translate(0, 0, k * 1.1), WOOD, [0.7, 1]));
    parts.push(paint(xf(box(1.6, 0.9, 2.1), [0, 0.55, 0]), 0x5a4030, [0.7, 1.05]));
    parts.push(paint(xf(box(1.75, 0.12, 2.25), [0, 1.42, 0]), IRON, [1, 1]));
    for (const [x, z] of [[-0.75, -0.7], [0.75, -0.7], [-0.75, 0.7], [0.75, 0.7]] as const) parts.push(paint(xf(cyl(0.32, 0.32, 0.14, 10), [x, 0.32, z], [0, 0, Math.PI / 2]), IRON, [0.8, 1]));
    parts.push(paint(new THREE.SphereGeometry(0.75, 8, 5, 0, Math.PI * 2, 0, Math.PI / 2).scale(1, 0.55, 1.25).translate(0, 1.45, 0), 0x6e6656, [0.8, 1.1]));
    parts.push(paint(new THREE.SphereGeometry(0.22, 6, 4).translate(0.3, 1.75, 0.3), JADE_L, [1, 1]));
    return combine(parts);
  },
  ore_pile: () => {
    const parts: THREE.BufferGeometry[] = [];
    parts.push(paint(new THREE.SphereGeometry(1.6, 9, 5, 0, Math.PI * 2, 0, Math.PI / 2).scale(1.1, 0.55, 0.9), 0x5f574a, [0.6, 1]));
    for (let i = 0; i < 6; i++) parts.push(paint(new THREE.DodecahedronGeometry(0.28 + (i % 3) * 0.08).translate(Math.cos(i * 1.7) * 1.0, 0.55 + (i % 2) * 0.2, Math.sin(i * 1.7) * 0.7), i % 3 === 0 ? JADE_L : 0x7a7060, [0.8, 1.1]));
    parts.push(paint(xf(box(0.12, 0.12, 2.2), [1.2, 0.5, 0.2], [0.4, 0.6, 0]), WOOD, [0.8, 1]));
    parts.push(paint(xf(box(0.5, 0.25, 0.1), [1.55, 0.95, 0.9], [0.4, 0.6, 0]), IRON, [0.8, 1]));
    return combine(parts);
  },
  sarcophagus: () => {
    const parts: THREE.BufferGeometry[] = [];
    parts.push(paint(box(1.6, 0.9, 3.4), SAND_D, [0.6, 1]));
    parts.push(paint(box(1.8, 0.3, 3.6).translate(0, 0.9, 0), SAND, [0.85, 1.05]));
    parts.push(paint(xf(box(0.9, 0.12, 2.6), [0, 1.22, 0]), SAND_L, [1, 1.05]));
    parts.push(serpentTube([new THREE.Vector3(-0.4, 1.3, -1.4), new THREE.Vector3(0.4, 1.35, -0.4), new THREE.Vector3(-0.3, 1.35, 0.6), new THREE.Vector3(0.1, 1.4, 1.4)], 0.13, JADE, 20, 6, 0.6));
    return combine(parts);
  },
  timber_post: () => paint(box(0.5, 4, 0.5), WOOD, [0.6, 1.05]),
  column_serpent: () => serpentColumn(SAND_L),
  column_gold: () => serpentColumn(GOLD),
  fern: () => {
    // A low tuft of arching blades (reads as a plant from the top-down camera, unlike a flat leaf card).
    const parts: THREE.BufferGeometry[] = [];
    const n = 7;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + (i % 2) * 0.3;
      const len = 0.55 + (i % 3) * 0.12;
      const blade = new THREE.ConeGeometry(0.11, len, 4).scale(1, 1, 0.35).translate(0, len / 2, 0);
      blade.rotateX(0.95 + (i % 2) * 0.2).rotateY(a);
      parts.push(paint(blade, i % 3 === 0 ? 0x3d6b2e : 0x2f5a2a, [0.55, 1.1]));
    }
    parts.push(paint(sphere(0.14, 6, 4).scale(1, 0.6, 1), 0x24401f, [0.7, 1]));
    return combine(parts);
  },
  rubble: () => {
    // A small heap of broken sandstone blocks, one with a jade sliver (serpent temple debris).
    const parts: THREE.BufferGeometry[] = [];
    parts.push(paint(xf(box(0.7, 0.32, 0.5), [0, 0, 0], [0, 0.4, 0.06]), SAND_D, [0.65, 1]));
    parts.push(paint(xf(box(0.45, 0.26, 0.4), [0.55, 0, 0.25], [0.1, 1.2, 0]), SAND, [0.7, 1.05]));
    parts.push(paint(xf(box(0.36, 0.2, 0.3), [-0.45, 0, 0.3], [0, 0.3, 0.2]), SAND_L, [0.75, 1.05]));
    parts.push(paint(xf(box(0.3, 0.18, 0.26), [0.15, 0.3, 0.05], [0.2, 0.8, 0.1]), SAND, [0.8, 1.05]));
    parts.push(paint(xf(box(0.22, 0.08, 0.5), [-0.2, 0.0, -0.4], [0, -0.5, 0]), JADE, [0.8, 1]));
    return combine(parts);
  },
  // Lidded clay urn (replaces Kenney's 6-sided pot, which read as a purple-outlined hexagon from the
  // top-down camera): 16-sided smooth body, jade band and lid with a gold knob, so the top view is a
  // round jade disc on a terracotta ring.
  urn: () => urn(),
  // The same urn tipped on its side (debris; ≤ 0.8 yd tall), mouth spilling dark soil.
  urn_tipped: () => {
    const g = urn(false);
    xf(g, [0, 0.38, -0.3], [Math.PI / 2 - 0.12, 0, 0]);
    const spill = paint(sphere(0.3, 10, 5).scale(1.2, 0.18, 1.4).translate(0, 0.02, 0.75), 0x3a2c20, [0.8, 1]);
    return combine([g, spill]);
  },
  stake: () => {
    const shaft = paint(xf(cyl(0.09, 0.12, 1.6, 6), [0, 0, 0], [0.18, 0, 0.1]), WOOD, [0.6, 1]);
    const tip = paint(xf(cone(0.12, 0.35, 6), [0.16, 1.55, 0.26], [0.18, 0, 0.1]), 0xb8a888, [1, 1]);
    return combine([shaft, tip]);
  },
  chain: () => {
    const parts: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 6; i++) parts.push(paint(xf(new THREE.TorusGeometry(0.16, 0.045, 5, 10), [0, 1.9 - i * 0.26, 0.12], [0, i % 2 ? Math.PI / 2 : 0, 0]), IRON, [0.9, 1.1]));
    parts.push(paint(box(0.3, 0.3, 0.15).translate(0, 1.95, 0.05), IRON, [1, 1]));
    return combine(parts);
  },
};

const CLAY = 0x9a5d3b;
const CLAY_D = 0x6e3f28;

function urn(lid = true): THREE.BufferGeometry {
  const prof = [
    [0.0, 0.0], [0.2, 0.0], [0.3, 0.07], [0.38, 0.22], [0.41, 0.38], [0.38, 0.54], [0.3, 0.66], [0.21, 0.73], [0.2, 0.78], [0.25, 0.82], [0.26, 0.86], [0.2, 0.87],
  ].map(([x, y]) => new THREE.Vector2(x, y));
  const parts: THREE.BufferGeometry[] = [];
  parts.push(paint(new THREE.LatheGeometry(prof, 16), CLAY, [0.62, 1.08], [0, 0.87]));
  parts.push(paint(cyl(0.415, 0.415, 0.07, 16).translate(0, 0.4, 0), JADE, [0.95, 1.05]));
  parts.push(paint(cyl(0.2, 0.2, 0.025, 16).translate(0, 0.83, 0), CLAY_D, [1, 1]));
  if (lid) {
    parts.push(paint(cyl(0.12, 0.25, 0.08, 16).translate(0, 0.85, 0), JADE_L, [0.85, 1.05]));
    parts.push(paint(sphere(0.07, 10, 6).translate(0, 0.97, 0), GOLD, [0.9, 1.1]));
  }
  return combine(parts);
}

function serpentColumn(capColor: Col): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  parts.push(paint(box(1.5, 0.5, 1.5), SAND_D, [0.65, 1]));
  parts.push(paint(cyl(0.55, 0.62, 3.2, 10).translate(0, 0.5, 0), SAND, [0.75, 1.05]));
  parts.push(paint(new THREE.TorusGeometry(0.66, 0.16, 6, 14).rotateX(Math.PI / 2).translate(0, 1.4, 0), JADE, [1, 1]));
  parts.push(paint(new THREE.TorusGeometry(0.64, 0.16, 6, 14).rotateX(Math.PI / 2).translate(0, 2.6, 0), JADE, [1, 1]));
  parts.push(paint(box(1.4, 0.4, 1.4).translate(0, 3.7, 0), capColor, [0.9, 1.05]));
  parts.push(...cobraHead(0.5, capColor).map((g) => xf(g, [0, 4.0, 0])));
  return combine(parts);
}

export const VENOM_PROC_NAMES = Object.keys(GEN);

/** Build (and cache in `cache`) one procedural prop by name; null for unknown names. */
export function venomProc(name: string, cache: Map<string, THREE.BufferGeometry | null>): THREE.BufferGeometry | null {
  if (cache.has(name)) return cache.get(name)!;
  const gen = GEN[name];
  const g = gen ? gen() : null;
  cache.set(name, g);
  return g;
}
